import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Upload, Sparkles, Camera, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { PLANT_CATALOG, PLANT_CATEGORIES, PLANT_EMOJIS, STAGES, SOW_WINDOWS } from '../lib/data';
import { fileToCompressedDataURL } from '../lib/image';
import ImagePicker from './ImagePicker';
import AiCaution from './AiCaution';
import BatchIdentify from './BatchIdentify';
import ImageCropper from './ImageCropper';
import PlantIdentityPath from './PlantIdentityPath';
import PlantVarietyPicker from './PlantVarietyPicker';
import { pickerCatalog, pickerSpeciesKey, speciesVarieties } from '../lib/plantPicker';
import { cultivarMatches, NAMED_CULTIVAR_COUNT } from '../lib/cultivars';
import PLANT_VARIETY_PRESETS from '../data/legacyVarietyPresets.json';
import { useI18n } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { getClimate, sowStatus } from '../lib/climate';
import { toast } from 'sonner';
import SpeciesIcon from './species/SpeciesIcon';

// Lightweight translations for plant catalog attribute strings stored in Danish.
const SUN_TR = {
    en: { 'Sol': 'Sun', 'Halvskygge': 'Part shade', 'Skygge': 'Shade' },
    de: { 'Sol': 'Sonne', 'Halvskygge': 'Halbschatten', 'Skygge': 'Schatten' },
    sv: { 'Sol': 'Sol', 'Halvskygge': 'Halvskugga', 'Skygge': 'Skugga' },
    no: { 'Sol': 'Sol', 'Halvskygge': 'Halvskygge', 'Skygge': 'Skygge' },
};
const WATER_TR = {
    en: { 'Højt': 'High', 'Medium': 'Medium', 'Lavt': 'Low' },
    de: { 'Højt': 'Hoch', 'Medium': 'Mittel', 'Lavt': 'Niedrig' },
    sv: { 'Højt': 'Hög', 'Medium': 'Medel', 'Lavt': 'Låg' },
    no: { 'Højt': 'Høyt', 'Medium': 'Middels', 'Lavt': 'Lavt' },
};
const trAttr = (val, map, locale) => (locale && map[locale] && map[locale][val]) || val;
const normalizeText = (value) => String(value || '').trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

const buildSpeciesNameMap = () => {
    const out = [];
    const seen = new Set();
    for (const plant of PLANT_CATALOG) {
        const names = [
            plant.id,
            plant.scientific_name,
            plant.name?.da,
            plant.name?.en,
            plant.name?.de,
            plant.name?.sv,
            plant.name?.no,
        ].filter(Boolean);
        const unique = Array.from(new Set(names.map((name) => String(name))));
        for (const name of unique) {
            const key = normalizeText(name);
            if (!key || seen.has(key)) continue;
            out.push({ key, name, plantId: plant.id });
            seen.add(key);
        }
    }
    return out;
};

const SPECIES_NAME_MAP = buildSpeciesNameMap();
const slugifySpecies = (value) => normalizeText(value)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64) || 'custom_plant';

const resolveSpeciesId = (raw, fallback = '') => {
    const key = normalizeText(raw);
    if (!key) return fallback || '';
    const byId = PLANT_CATALOG.find((p) => normalizeText(p.id) === key);
    if (byId) return byId.id;
    const byName = SPECIES_NAME_MAP.find((row) => row.key === key);
    if (byName) return byName.plantId;
    return slugifySpecies(raw);
};

const getSpeciesSuggestions = (locale) => {
    const localeName = (v) => v[locale] || v.en || v.da || '';
    const map = new Map();
    for (const p of pickerCatalog(PLANT_CATALOG)) {
        const label = localeName(p.name);
        if (!map.has(normalizeText(label))) map.set(normalizeText(label), { id: p.id, label });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
};

export default function AddPlantModal({ open, onClose, onAdded }) {
    const { t, locale } = useI18n();
    const { user } = useAuth();
    const climate = getClimate(user?.location?.lat);
    const navigate = useNavigate();
    const [tab, setTab] = useState('catalog');
    const [filter, setFilter] = useState('all');
    const [customCategory, setCustomCategory] = useState('');
    const [catalogSearch, setCatalogSearch] = useState('');
    const [name, setName] = useState('');
    const [emoji, setEmoji] = useState('🌱');
    const [stage, setStage] = useState('Seedling');
    const [location, setLocation] = useState('');
    const [notes, setNotes] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [snaps, setSnaps] = useState([]); // [{file, preview}] — up to 3 angles for Photo ID
    const [busy, setBusy] = useState(false);
    const [identifying, setIdentifying] = useState(false);
    const [candidates, setCandidates] = useState(null);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [speciesCommonName, setSpeciesCommonName] = useState('');
    const [variety, setVariety] = useState('');
    const [speciesInputId, setSpeciesInputId] = useState('');
    const [waterInterval, setWaterInterval] = useState(3);
    const [cropFile, setCropFile] = useState(null);
    const [varietyCatalog, setVarietyCatalog] = useState(null);
    const [addingOwnVariety, setAddingOwnVariety] = useState(false);
    const isPremium = !!user?.premium || !!user?.ai_payg_available; // photo-AI access only; server charges confirmed usage
    const MAX_SNAPS = 3;
    const speciesOptions = getSpeciesSuggestions(locale);
    const selectedSpecies = PLANT_CATALOG.find(p => p.id === pickerSpeciesKey(speciesInputId));
    const selectedCategory = selectedSpecies?.cat || customCategory;
    const categoryLabel = id => t(`plant_picker.category_${id}`);
    const catalogVarieties = speciesVarieties(speciesInputId, varietyCatalog, PLANT_VARIETY_PRESETS);
    const searchableVarieties = Object.fromEntries(pickerCatalog(PLANT_CATALOG).map(p => [p.id, speciesVarieties(p.id, varietyCatalog, PLANT_VARIETY_PRESETS)]));
    const catalogPlants = pickerCatalog(PLANT_CATALOG, { search: catalogSearch, category: filter, varieties: searchableVarieties });

    useEffect(() => {
        if (!open || varietyCatalog) return;
        api.get('/plant-varieties').then((r) => setVarietyCatalog(r.data)).catch(() => {});
    }, [open, varietyCatalog]);

    if (!open) return null;

    const reset = () => {
        setName(''); setSpeciesCommonName(''); setVariety(''); setSpeciesInputId('');
        setEmoji('🌱'); setStage('Seedling'); setLocation(''); setNotes('');
        setWaterInterval(3);
        setImageFile(null); setSnaps([]); setCandidates(null); setSelectedIdx(0); setCropFile(null); setTab('catalog'); setCatalogSearch('');
        setAddingOwnVariety(false);
        setFilter('all'); setCustomCategory('');
    };

    const handlePlantApiError = (err, fallback = 'Could not add') => {
        const d = err?.response?.data?.detail;
        if (err?.response?.status === 402) {
            toast.error(typeof d === 'string' ? d : 'Plant limit reached. Upgrade to Premium.');
            onClose?.();
            navigate('/premium');
            return true;
        }
        toast.error(typeof d === 'string' ? d : fallback);
        return false;
    };

    const pickCatalogEntry = (entry) => {
        const commonName = entry.name?.[locale] || entry.name?.en || entry.name?.da || '';
        setSpeciesInputId(entry.id);
        setCustomCategory(entry.cat); setFilter(entry.cat);
        setSpeciesCommonName(commonName);
        setVariety('');
        setAddingOwnVariety(false);
        setName(commonName);
        setEmoji(entry.emoji || '🌱');
        setStage(entry.cat === 'mushroom' ? 'Growing' : 'Seedling');
        setWaterInterval(entry.interval || 3);
        setImageFile(null);
        setSnaps([]);
        setCandidates(null);
        setSelectedIdx(0);
        setTab('custom');
    };

    const pickCustomPlant = () => {
        setCustomCategory(filter === 'all' ? '' : filter);
        setSpeciesCommonName('');
        setVariety('');
        setSpeciesInputId('');
        setAddingOwnVariety(false);
        setName('');
        setEmoji('🌱');
        setStage('Seedling');
        setWaterInterval(3);
        setLocation('');
        setNotes('');
        setImageFile(null);
        setSnaps([]);
        setCandidates(null);
        setSelectedIdx(0);
        setTab('custom');
    };

    const onPickImage = async (e) => {
        const f = e?.target ? e.target.files?.[0] : e;
        if (!f) return;
        setImageFile(f);
        setCandidates(null); setSelectedIdx(0);
    };

    const prepareSnapImage = (f) => {
        if (f) setCropFile(f);
    };

    const addSnapImage = async (f) => {
        if (!f) return;
        if (snaps.length >= 1 && !isPremium) {
            toast.error('Multi-photo ID is a Premium feature');
            onClose?.();
            navigate('/premium');
            return;
        }
        if (snaps.length >= MAX_SNAPS) { toast.error(`Up to ${MAX_SNAPS} photos`); return; }
        let preview;
        try {
            preview = await fileToCompressedDataURL(f);
        } catch {
            preview = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
        }
        setSnaps((s) => [...s, { file: f, preview }]);
        setCropFile(null);
        setCandidates(null); setSelectedIdx(0);
    };

    const removeSnap = (i) => {
        setSnaps((s) => s.filter((_, idx) => idx !== i));
        setCandidates(null); setSelectedIdx(0);
    };

    const applyCandidate = (c) => {
        const commonName = c?.species_common_name || c?.common_name || c?.name || '';
        if (c?.display_name || c?.name) setName(c?.display_name || c?.name);
        if (c?.emoji) setEmoji(c.emoji);
        if (c?.stage && STAGES.includes(c.stage)) setStage(c.stage);
        if (commonName) setSpeciesCommonName(commonName);
        setVariety(c?.variety || '');
        if (c?.water_interval_days) setWaterInterval(c.water_interval_days);
        setSpeciesInputId(c?.catalog_key || c?.species_key || resolveSpeciesId(commonName));
    };

    const identifyPhoto = async () => {
        if (!snaps.length) return;
        if (user?.is_guest) {
            toast.error('Opret en gratis profil for at bruge AI-identifikation 🌱');
            return;
        }
        setIdentifying(true);
        try {
            const r = await api.post('/ai/identify', {
                images_base64: snaps.map((s) => s.preview),
                mode: 'identify',
                locale,
            });
            const list = Array.isArray(r.data?.candidates) ? r.data.candidates : (r.data?.name ? [r.data] : []);
            if (!list.length) { toast.error('Could not identify — try another photo'); return; }
            setCandidates(list);
            setSelectedIdx(0);
            applyCandidate(list[0]);
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (err?.response?.status === 402) {
                toast.error(typeof d === 'string' ? d : 'Premium required');
                onClose?.();
                navigate('/premium');
                return;
            }
            if (err?.response?.status === 401 || err?.response?.status === 403) return; // global signup CTA handles it
            toast.error(typeof d === 'string' ? d : 'Could not identify');
        } finally { setIdentifying(false); }
    };

    const addCustom = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        try {
            const resolvedSpecies = speciesInputId || resolveSpeciesId(speciesCommonName || name);
            let image_path = null;
            const mainFile = snaps[0]?.file || imageFile;
            if (mainFile) {
                const fd = new FormData();
                fd.append('file', mainFile);
                const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                image_path = up.data.path;
            }
            const sel = candidates?.[selectedIdx];
            const payload = {
                name: name.trim(),
                display_name: name.trim(),
                species_common_name: speciesCommonName.trim() || (sel?.species_common_name || sel?.common_name || name).trim(),
                species: resolvedSpecies || undefined,
                category: selectedCategory || undefined,
                scientific_name: selectedSpecies?.scientific_name || sel?.scientific_name || undefined,
                variety: variety.trim() || undefined,
                emoji, stage, location, notes, image_path,
                care: sel?.care || undefined,
                sun: sel?.sun || undefined,
                water: sel?.water || undefined,
                ai_confidence: typeof sel?.confidence === 'number' ? sel.confidence : undefined,
                water_interval_days: waterInterval || sel?.water_interval_days || 3,
            };
            const r = await api.post('/plants', payload);
            toast.success(`${r.data.name} added 🌱`);
            onAdded?.(r.data);
            reset(); onClose?.();
        } catch (err) { handlePlantApiError(err); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="add-plant-modal" className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-brand-ink/40 p-0 sm:p-4 backdrop-blur-sm">
            <div className="bg-brand-bg w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border border-brand-border max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-brand-bg flex items-center justify-between px-5 py-4 border-b border-brand-border z-10">
                    <h3 className="font-serif text-xl text-brand-ink">{t('garden.add')}</h3>
                    <button data-testid="btn-close-add-plant" onClick={onClose} aria-label={t('common.close')} className="p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center hover:bg-brand-bg2 rounded-full">
                        <X size={18} strokeWidth={1.6} />
                    </button>
                </div>
                <div className="px-5 pt-4">
                    <div className="inline-flex gap-1 bg-brand-bg2 p-1 rounded-full mb-4 text-xs">
                        <button data-testid="tab-catalog" onClick={() => setTab('catalog')} className={`px-3 py-1.5 rounded-full font-medium ${tab === 'catalog' ? 'bg-brand-green text-white' : 'text-brand-ink2'}`}>Catalog</button>
                        <button data-testid="tab-snap" onClick={() => setTab('snap')} className={`px-3 py-1.5 rounded-full font-medium inline-flex items-center gap-1 ${tab === 'snap' ? 'bg-brand-green text-white' : 'text-brand-ink2'}`}><Sparkles size={11} /> Photo ID</button>
                        <button data-testid="tab-multi" onClick={() => setTab('multi')} className={`px-3 py-1.5 rounded-full font-medium inline-flex items-center gap-1 ${tab === 'multi' ? 'bg-brand-green text-white' : 'text-brand-ink2'}`}><Camera size={11} /> Multi</button>
                        <button data-testid="tab-custom" onClick={() => setTab('custom')} className={`px-3 py-1.5 rounded-full font-medium ${tab === 'custom' ? 'bg-brand-green text-white' : 'text-brand-ink2'}`}>Manual</button>
                    </div>
                </div>

                {tab === 'catalog' && (
                    <div className="px-5 pb-6">
                        <PlantIdentityPath category={filter === 'all' ? '' : categoryLabel(filter)}
                            onCategory={filter === 'all' ? undefined : () => { setFilter('all'); setCatalogSearch(''); }} />
                        <h4 className="font-semibold mb-3">{t(filter === 'all' ? 'plant_picker.choose_category' : 'plant_picker.choose_species')}</h4>
                        {filter === 'mushroom' && <p data-testid="mushroom-caution" className="text-sm mb-4 p-3 rounded-xl bg-amber-50 text-amber-950">{t('plant_picker.mushroom_caution')}</p>}
                        <p className="text-xs text-brand-ink3 mb-3">{NAMED_CULTIVAR_COUNT} {t('plant_picker.catalog_count')}</p>
                        <input type="search" data-testid="catalog-search" value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)}
                            placeholder={t('plant_picker.search_species')} aria-label={t('plant_picker.search_species')}
                            className="w-full mb-4 bg-brand-card border border-brand-border rounded-2xl px-4 py-3" />
                        {(filter !== 'all' || catalogSearch.trim()) && <div className="flex justify-between items-center mb-3 text-sm"><span role="status">{catalogPlants.length} {t('plant_picker.species_results')}</span><button type="button" onClick={() => { setFilter('all'); setCatalogSearch(''); }}>{t('plant_picker.clear_filters')}</button></div>}
                        {filter === 'all' && <div className="grid grid-cols-2 gap-3 mb-4">
                            {PLANT_CATEGORIES.filter(c => c.id !== 'all').map(c => (
                                <button key={c.id} data-testid={`cat-filter-${c.id}`} onClick={() => setFilter(c.id)}
                                    className="min-h-20 flex flex-col items-start gap-2 p-4 rounded-2xl text-sm font-medium border border-brand-border bg-brand-card">
                                    <span className="text-2xl" aria-hidden="true">{c.emoji}</span>
                                    <span>{categoryLabel(c.id)} →</span>
                                </button>
                            ))}
                        </div>}
                        {(filter !== 'all' || catalogSearch.trim()) && !catalogPlants.length && <p className="text-sm mb-3" data-testid="catalog-no-results">{t('plant_picker.no_results')}</p>}
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={pickCustomPlant}
                                className="col-span-2 border border-dashed border-brand-border bg-brand-bg2 text-left rounded-2xl p-4 hover:border-brand-green transition-all"
                            >
                                <div className="font-semibold">{t('plant_picker.custom')}</div>
                                <div className="text-xs text-brand-ink3 mt-1">{t('plant_picker.custom_hint')}</div>
                            </button>
                            {(filter !== 'all' || catalogSearch.trim()) && catalogPlants.map((p) => {
                                const sow = sowStatus(SOW_WINDOWS[p.id], climate);
                                return (
                                <button
                                    key={p.id}
                                    data-testid={`catalog-${p.id}`}
                                    disabled={busy}
                                    onClick={() => pickCatalogEntry(p)}
                                    className="text-left bg-brand-card border border-brand-border rounded-2xl p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all"
                                >
                                    <div className="flex items-start justify-between">
                                        <SpeciesIcon kind="plant" id={p.id} emoji={p.emoji} name={p.name[locale] || p.name.en} size={52} className="mb-2" />
                                        {sow && (
                                            <span
                                                data-testid={`sow-chip-${p.id}`}
                                                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${sow === 'now' ? 'bg-brand-green/15 text-brand-green' : 'bg-amber-500/15 text-amber-600'}`}
                                            >
                                                {sow === 'now' ? `🌱 ${t('sow.now')}` : t('sow.soon')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-serif text-base text-brand-ink">{p.name[locale] || p.name.en || p.name.da}</div>
                                    <p className="text-xs text-brand-ink3">{categoryLabel(p.cat)}</p>
                                    {!!catalogSearch.trim() && <p className="text-xs mt-1 text-brand-green">{searchableVarieties[p.id].filter(option => cultivarMatches(option, catalogSearch)).slice(0, 3).map(option => option.label).join(' · ')}</p>}
                                    {p.scientific_name && <p className="text-xs italic text-brand-ink3 mt-1">{p.scientific_name}</p>}
                                    {p.id === 'tomat' && <p className="text-xs text-brand-ink3 mt-1">{t('plant_picker.tomato_hint')}</p>}
                                    {(p.sun || p.water) ? <div className="text-[10px] uppercase tracking-widest text-brand-ink3 mt-1 font-semibold">{[trAttr(p.sun, SUN_TR, locale), trAttr(p.water, WATER_TR, locale)].filter(Boolean).join(' · ')}</div>
                                        : <p className="text-xs text-brand-ink3 mt-1">{p.info?.[locale] || p.info?.en}</p>}
                                </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {tab === 'snap' && (
                    <div className="px-5 pb-6 space-y-4">
                        <p className="text-sm text-brand-ink2">Snap a photo of your plant — AI will identify it and pre-fill the fields.{isPremium ? ' Add up to 3 angles for the most accurate match.' : ''}</p>
                        <AiCaution />

                        {snaps.length > 0 && (
                            <div className="grid grid-cols-3 gap-2" data-testid="snap-previews">
                                {snaps.map((s, i) => (
                                    <div key={s.preview || i} className="relative">
                                        <img src={s.preview} alt="" className="w-full aspect-square object-cover rounded-2xl border border-brand-border" />
                                        <button onClick={() => removeSnap(i)} className="absolute top-1 right-1 bg-brand-ink/70 text-white p-1 rounded-full hover:bg-brand-ink">
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {snaps.length < MAX_SNAPS && (
                            <ImagePicker testId="addplant-pick" onSelect={prepareSnapImage}>
                                {(openPicker) => (
                                    <button
                                        data-testid="btn-pick-photo"
                                        onClick={openPicker}
                                        className="block w-full bg-brand-card border-2 border-dashed border-brand-border rounded-3xl p-8 text-center cursor-pointer hover:border-brand-green transition-colors"
                                    >
                                        <Camera size={28} strokeWidth={1.5} className="mx-auto text-brand-green mb-2" />
                                        <div className="text-sm font-medium text-brand-ink">
                                            {snaps.length ? `Add another angle (${snaps.length}/${MAX_SNAPS})` : 'Take photo or choose from gallery'}
                                        </div>
                                        <div className="text-xs text-brand-ink3 mt-1">JPG · PNG · WEBP</div>
                                    </button>
                                )}
                            </ImagePicker>
                        )}

                        {!isPremium && snaps.length >= 1 && (
                            <p className="text-[11px] text-brand-ochre" data-testid="snap-premium-hint">📸 Add up to 3 angles for a more accurate ID — <button onClick={() => { onClose?.(); navigate('/premium'); }} className="underline font-medium">unlock Premium</button>.</p>
                        )}

                        {snaps.length > 0 && (
                            <div className="space-y-3">
                                {!candidates && (
                                    <button data-testid="btn-identify" onClick={identifyPhoto} disabled={identifying} className="w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                                        {identifying ? <><Loader2 size={15} className="animate-spin" /> Analyzing…</> : <><Sparkles size={15} /> Identify with AI</>}
                                    </button>
                                )}
                                {candidates && candidates.length > 0 && (() => {
                                    const sel = candidates[selectedIdx] || candidates[0];
                                    return (
                                    <div data-testid="identification-result" className="space-y-3">
                                        {candidates.length > 1 && (
                                            <div>
                                                <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-1.5">Not right? Pick the best match</p>
                                                <div className="flex gap-2 overflow-x-auto pb-1" data-testid="candidate-options">
                                                    {candidates.map((c, i) => (
                                                        <button
                                                            key={c.name || c.species || i}
                                                            data-testid={`candidate-${i}`}
                                                            onClick={() => { setSelectedIdx(i); applyCandidate(c); }}
                                                            className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${i === selectedIdx ? 'bg-brand-green text-white border-brand-green' : 'bg-brand-card text-brand-ink border-brand-border hover:border-brand-green/50'}`}
                                                        >
                                                            <span className="text-base leading-none">{c.emoji || '🌱'}</span>
                                                            <span className="truncate max-w-[120px]">{c.name}</span>
                                                            {typeof c.confidence === 'number' && <span className={`text-[10px] ${i === selectedIdx ? 'text-white/80' : 'text-brand-ink3'}`}>{c.confidence}%</span>}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4 space-y-2">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <SpeciesIcon wiki={sel.species} emoji={sel.emoji || '🌱'} name={sel.name} size={64} />
                                                    <h4 className="font-serif text-xl text-brand-ink mt-1">{sel.name || 'Unknown plant'}</h4>
                                                    {(sel.scientific_name || sel.species) && <p className="text-xs italic text-brand-ink3">{sel.scientific_name || sel.species}</p>}
                                                </div>
                                                {typeof sel.confidence === 'number' && (
                                                    <span className="text-[10px] uppercase tracking-widest font-semibold bg-brand-sage/20 text-brand-green px-2 py-1 rounded-full">{sel.confidence}% confident</span>
                                                )}
                                            </div>
                                            {sel.care && <p className="text-sm text-brand-ink2 leading-relaxed">{sel.care}</p>}
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                {sel.sun && <span className="text-xs bg-brand-bg2 px-3 py-1 rounded-full">☀️ {sel.sun}</span>}
                                                {sel.water && <span className="text-xs bg-brand-bg2 px-3 py-1 rounded-full">💧 {sel.water}</span>}
                                                {sel.water_interval_days && <span className="text-xs bg-brand-bg2 px-3 py-1 rounded-full">🗓️ Water every {sel.water_interval_days} days</span>}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button data-testid="btn-reidentify" onClick={identifyPhoto} disabled={identifying} className="inline-flex items-center justify-center gap-1.5 bg-brand-bg2 text-brand-ink px-4 py-3 rounded-full text-sm hover:bg-brand-border disabled:opacity-50">
                                                {identifying ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Identify again
                                            </button>
                                            <button type="button" data-testid="btn-correct-identification" onClick={() => setTab('custom')} className="inline-flex items-center justify-center bg-brand-bg2 text-brand-ink px-3 py-3 rounded-full text-sm hover:bg-brand-border">
                                                Correct details
                                            </button>
                                            <button data-testid="btn-add-identified" onClick={addCustom} disabled={busy} className="flex-1 bg-brand-green text-white py-3 rounded-full font-medium disabled:opacity-50">
                                                {busy ? 'Saving…' : 'Add to my garden'}
                                            </button>
                                        </div>
                                    </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'multi' && <BatchIdentify onClose={onClose} onAdded={onAdded} />}

                <ImageCropper file={cropFile} onCancel={() => setCropFile(null)} onCrop={addSnapImage} />

                {tab === 'custom' && (
                    <form onSubmit={addCustom} className="px-5 pb-6 space-y-4">
                        <PlantIdentityPath category={selectedCategory ? categoryLabel(selectedCategory) : ''} species={speciesCommonName} variety={variety}
                            onCategory={() => { setFilter('all'); setTab('catalog'); }}
                            onSpecies={() => { setFilter(selectedCategory || 'all'); setCatalogSearch(''); setTab('catalog'); }} />
                        <p className="text-xs text-brand-ink3">
                            {t('plant_picker.identity_hint')}
                        </p>
                        <label className="block text-sm">{t('plant_picker.choose_category')}
                            <select data-testid="input-plant-category" value={selectedCategory} required
                                onChange={e => {
                                    setCustomCategory(e.target.value);
                                    if (selectedSpecies && selectedSpecies.cat !== e.target.value) {
                                        if (name === speciesCommonName) setName('');
                                        setSpeciesInputId(''); setSpeciesCommonName(''); setVariety(''); setAddingOwnVariety(false);
                                        setCandidates(null); setSelectedIdx(0);
                                    }
                                }} className="mt-1 w-full rounded-xl border border-brand-border bg-brand-card px-4 py-3">
                                <option value="">{t('plant_picker.choose_category')}</option>
                                {[...PLANT_CATEGORIES.filter(c => c.id !== 'all').map(c => c.id), 'other'].map(id => <option key={id} value={id}>{categoryLabel(id)}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Species / Type (common name)</span>
                            <input
                                data-testid="input-plant-species"
                                list="species-name-options"
                                value={speciesCommonName}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (pickerSpeciesKey(resolveSpeciesId(value)) !== pickerSpeciesKey(speciesInputId)) {
                                        setVariety(''); setAddingOwnVariety(false);
                                        setCandidates(null); setSelectedIdx(0);
                                    }
                                    setSpeciesCommonName(value);
                                    setSpeciesInputId(resolveSpeciesId(value));
                                }}
                                required
                                className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3"
                                placeholder="Tomato, cherry tomato, basil..."
                            />
                            <datalist id="species-name-options">
                                {speciesOptions.map((s) => <option key={s.id} value={s.label} />)}
                            </datalist>
                        </label>
                        <PlantVarietyPicker key={speciesInputId} speciesLabel={speciesCommonName.trim()} options={catalogVarieties}
                            initialSearch={catalogSearch.trim() && catalogVarieties.some(option => cultivarMatches(option, catalogSearch)) ? catalogSearch : ''}
                            value={variety} onChange={setVariety} addingOwn={addingOwnVariety} setAddingOwn={setAddingOwnVariety} />
                        {selectedCategory === 'mushroom' && <p data-testid="mushroom-caution" className="text-sm p-3 rounded-xl bg-amber-50 text-amber-950">{t('plant_picker.mushroom_caution')}</p>}
                        {['nut', 'mushroom'].includes(selectedCategory) && <p className="text-sm text-brand-ink3">{t('plant_picker.care_setup')}</p>}
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('plant_picker.personal_name')}</span>
                            <input data-testid="input-plant-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3" placeholder={t('plant_picker.name_placeholder')} />
                        </label>
                        <div>
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Emoji</span>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                                {PLANT_EMOJIS.map((em) => (
                                    <button type="button" key={em} onClick={() => setEmoji(em)} className={`text-xl w-10 h-10 rounded-xl border ${emoji === em ? 'bg-brand-green border-brand-green' : 'bg-brand-card border-brand-border'}`}>{em}</button>
                                ))}
                            </div>
                        </div>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('garden.stage')}</span>
                            <select data-testid="select-stage" value={stage} onChange={(e) => setStage(e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3">
                                {STAGES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Location</span>
                            <input value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3" placeholder="Greenhouse, windowsill..." />
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Water interval (days)</span>
                            <input
                                type="number"
                                min={2}
                                max={14}
                                value={waterInterval}
                                onChange={(e) => setWaterInterval(parseInt(e.target.value || '3', 10))}
                                className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('garden.notes')}</span>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="3" className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3 resize-none" />
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Image (optional)</span>
                            <div className="mt-1 flex items-center gap-3">
                                <label className="inline-flex items-center gap-2 bg-brand-bg2 hover:bg-brand-border px-4 py-2.5 rounded-full cursor-pointer">
                                    <Upload size={14} strokeWidth={1.8} />
                                    <span className="text-sm">{imageFile?.name || 'Choose image'}</span>
                                    <input data-testid="input-plant-image" type="file" accept="image/*" onChange={onPickImage} className="hidden" />
                                </label>
                            </div>
                        </label>
                        <button data-testid="btn-submit-plant" disabled={busy} type="submit" className="w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium disabled:opacity-50">
                            {busy ? 'Saving…' : t('common.save')}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
